// Lugar de atención y checklist del estado físico del equipo (prueba reversible, ver docs/ESTADO_FISICO.md).
// En el taller lo registra el personal al crear la orden; a domicilio, el técnico en la primera visita.

/**
 * El checklist del estado físico está apagado: se decidió no usarlo por ahora, ni en el taller ni a domicilio.
 * El código queda listo; con `true` se vuelve a pedir. El lugar de atención y el PDF de recepción siguen activos.
 */
export const PHYSICAL_CHECK_ENABLED = false as boolean;

/** Largo máximo de la descripción del estado del equipo y los accesorios que se reciben en el taller. */
export const RECEPTION_NOTES_MAX = 1000;

export type ServiceLocation = 'taller' | 'domicilio';
export type CheckStatus = 'bien' | 'con_observacion' | 'no_aplica';

export const SERVICE_LOCATION_LABELS: Record<ServiceLocation, string> = {
  taller: 'Taller',
  domicilio: 'Domicilio',
};

export const CHECK_STATUS_LABELS: Record<CheckStatus, string> = {
  bien: 'Bien',
  con_observacion: 'Con observación',
  no_aplica: 'No aplica',
};

export interface CheckPoint {
  id: string;
  label: string;
}

/** Puntos para todos los equipos. */
const GENERAL: CheckPoint[] = [
  { id: 'carcasa', label: 'Carcasa o cuerpo (golpes, abolladuras, fisuras, rayones)' },
  { id: 'acabado', label: 'Pintura y acabado (óxido, manchas, decoloración)' },
  { id: 'controles', label: 'Panel de control, perillas y botones' },
  { id: 'pantalla', label: 'Pantalla o display' },
  { id: 'cable', label: 'Cable de alimentación y enchufe' },
  { id: 'placa', label: 'Placa de datos (modelo y serie) legible' },
  { id: 'sellos', label: 'Sellos y tornillos sin abrir' },
  { id: 'reparacion', label: 'Sin señales de reparación anterior por terceros' },
  { id: 'limpieza', label: 'Limpieza (sin restos de comida, agua o suciedad)' },
  { id: 'olor', label: 'Sin olor a quemado, humedad ni plagas' },
  { id: 'enciende', label: 'Enciende al recibirlo' },
];

export type EquipmentGroup = 'refrigeracion' | 'lavado' | 'coccion' | 'climatizacion' | 'pequenos';

export const GROUP_LABELS: Record<EquipmentGroup | 'general', string> = {
  general: 'Todos los equipos',
  refrigeracion: 'Refrigeración',
  lavado: 'Lavado',
  coccion: 'Cocción',
  climatizacion: 'Climatización',
  pequenos: 'Pequeños electrodomésticos',
};

const GROUP_POINTS: Record<EquipmentGroup, CheckPoint[]> = {
  refrigeracion: [
    { id: 'puertas', label: 'Puertas y bisagras' },
    { id: 'empaques', label: 'Empaques de puerta' },
    { id: 'repisas', label: 'Repisas, vidrios y cajones' },
    { id: 'dispensador', label: 'Dispensador de hielo o agua' },
  ],
  lavado: [
    { id: 'puerta', label: 'Puerta o tapa y su vidrio' },
    { id: 'empaque', label: 'Empaque de puerta' },
    { id: 'mangueras', label: 'Mangueras de entrada y desagüe' },
    { id: 'filtro', label: 'Filtro' },
    { id: 'tambor', label: 'Tambor o cuba' },
    { id: 'base', label: 'Patas o base' },
  ],
  coccion: [
    { id: 'quemadores', label: 'Quemadores u hornillas' },
    { id: 'vidrio', label: 'Vidrio de la puerta' },
    { id: 'rejillas', label: 'Rejillas y bandejas' },
    { id: 'interior', label: 'Interior (grasa u óxido)' },
    { id: 'plato', label: 'Plato giratorio' },
    { id: 'filtros', label: 'Filtros de la campana' },
  ],
  climatizacion: [
    { id: 'unidad_interior', label: 'Unidad interior' },
    { id: 'unidad_exterior', label: 'Unidad exterior' },
    { id: 'aletas', label: 'Aletas y rejillas' },
    { id: 'serpentin', label: 'Serpentín' },
    { id: 'tuberia', label: 'Tubería y aislamiento' },
    { id: 'desague', label: 'Desagüe' },
    { id: 'soportes', label: 'Soportes' },
  ],
  pequenos: [
    { id: 'vaso', label: 'Vaso o jarra' },
    { id: 'tapa', label: 'Tapa' },
    { id: 'cuchillas', label: 'Cuchillas' },
    { id: 'acople', label: 'Base de acople' },
    { id: 'suela', label: 'Suela (plancha)' },
    { id: 'cepillos', label: 'Mangueras y cepillos (aspiradora)' },
  ],
};

const GROUP_BY_EQUIPMENT: Record<string, EquipmentGroup> = {
  REFRIGERADOR: 'refrigeracion',
  CONGELADOR: 'refrigeracion',
  VINERA: 'refrigeracion',
  'DISPENSADOR DE AGUA': 'refrigeracion',
  LAVADORA: 'lavado',
  SECADORA: 'lavado',
  LAVAPLATOS: 'lavado',
  COCINA: 'coccion',
  HORNO: 'coccion',
  MICROONDAS: 'coccion',
  'CAMPANA EXTRACTORA': 'coccion',
  'FREIDORA DE AIRE': 'coccion',
  SANDWICHERA: 'coccion',
  TOSTADORA: 'coccion',
  'AIRE ACONDICIONADO': 'climatizacion',
  ASPIRADORA: 'pequenos',
  BATIDORA: 'pequenos',
  CAFETERA: 'pequenos',
  EXPRIMIDOR: 'pequenos',
  HERVIDOR: 'pequenos',
  HIDROLAVADORA: 'pequenos',
  LICUADORA: 'pequenos',
  'OLLA A PRESIÓN': 'pequenos',
  PICATODO: 'pequenos',
  PLANCHA: 'pequenos',
  'TRAPEADOR A VAPOR': 'pequenos',
  VAPORIZADOR: 'pequenos',
};

/** Grupo del equipo elegido; null para "OTROS" o un equipo fuera del catálogo (solo los puntos generales). */
export function groupFor(equipment: string): EquipmentGroup | null {
  return GROUP_BY_EQUIPMENT[equipment.trim().toUpperCase()] ?? null;
}

export interface CheckSection {
  group: EquipmentGroup | 'general';
  points: CheckPoint[];
}

/** Puntos del estado del equipo: los generales y los de su grupo. */
export function checkSections(equipment: string): CheckSection[] {
  const group = groupFor(equipment);
  return [{ group: 'general', points: GENERAL }, ...(group ? [{ group, points: GROUP_POINTS[group] }] : [])];
}

/** Condiciones del lugar (solo a domicilio). */
export const SITE_POINTS: CheckPoint[] = [
  { id: 'toma', label: 'Toma eléctrica (voltaje y conexión a tierra)' },
  { id: 'agua', label: 'Conexión de agua y desagüe' },
  { id: 'gas', label: 'Conexión de gas' },
  { id: 'ventilacion', label: 'Ventilación y espacio' },
  { id: 'nivelacion', label: 'Nivelación' },
  { id: 'acceso', label: 'Acceso al equipo' },
];

/** Accesorios que se reciben (en el taller). */
export const ACCESSORIES: string[] = [
  'Manual',
  'Control remoto',
  'Bandejas',
  'Jarras o vasos',
  'Tapas',
  'Mangueras',
  'Filtros',
  'Caja original',
];

// Formulario --------------------------------------------------------------------------------------------------

export interface CheckForm {
  /** Estado de cada punto por id ('' = sin responder). Incluye las condiciones del lugar con el prefijo "site:". */
  statuses: Record<string, CheckStatus | ''>;
  notes: Record<string, string>;
  accessories: string[];
  otherAccessories: string;
  generalNotes: string;
}

export const EMPTY_CHECK: CheckForm = {
  statuses: {},
  notes: {},
  accessories: [],
  otherAccessories: '',
  generalNotes: '',
};

export const siteKey = (id: string) => `site:${id}`;

export type CheckErrors = Record<string, string>;

/** Errores por punto: sin responder, o con observación y sin describirla. */
export function validateCheck(form: CheckForm, equipment: string, location: ServiceLocation): CheckErrors {
  const errors: CheckErrors = {};
  const keys = [
    ...checkSections(equipment).flatMap((s) => s.points.map((p) => p.id)),
    ...(location === 'domicilio' ? SITE_POINTS.map((p) => siteKey(p.id)) : []),
  ];
  for (const key of keys) {
    const status = form.statuses[key] ?? '';
    if (status === '') errors[key] = 'Elige una opción.';
    else if (status === 'con_observacion' && (form.notes[key] ?? '').trim() === '')
      errors[key] = 'Describe la observación.';
  }
  return errors;
}

interface RecordItem {
  id: string;
  label: string;
  group: string;
  status: CheckStatus;
  note: string;
}

/** Payload de save_physical_check: los puntos con su etiqueta (el registro se lee igual aunque la lista cambie). */
export function toCheckPayload(form: CheckForm, equipment: string, location: ServiceLocation) {
  const item = (key: string, point: CheckPoint, group: string): RecordItem => ({
    id: point.id,
    label: point.label,
    group,
    status: (form.statuses[key] || 'no_aplica') as CheckStatus,
    note: (form.notes[key] ?? '').trim().toUpperCase(),
  });
  const other = form.otherAccessories.trim().toUpperCase();
  return {
    items: checkSections(equipment).flatMap((s) => s.points.map((p) => item(p.id, p, s.group))),
    site: location === 'domicilio' ? SITE_POINTS.map((p) => item(siteKey(p.id), p, 'lugar')) : [],
    accessories: location === 'taller' ? [...form.accessories, ...(other ? [other] : [])] : [],
    notes: form.generalNotes.trim().toUpperCase(),
  };
}

// Registro guardado --------------------------------------------------------------------------------------------

export interface PhysicalCheckRecord {
  moment: 'recepcion' | 'visita';
  items: RecordItem[];
  site: RecordItem[];
  accessories: string[];
  notes: string;
  filled_by: string;
  filled_by_name: string;
  filled_at: string;
}

/** El registro del servidor, si tiene la forma esperada. */
export function parseCheckRecord(value: unknown): PhysicalCheckRecord | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Partial<PhysicalCheckRecord>;
  if (!Array.isArray(record.items)) return null;
  return {
    moment: record.moment === 'visita' ? 'visita' : 'recepcion',
    items: record.items,
    site: Array.isArray(record.site) ? record.site : [],
    accessories: Array.isArray(record.accessories) ? record.accessories : [],
    notes: typeof record.notes === 'string' ? record.notes : '',
    filled_by: String(record.filled_by ?? ''),
    filled_by_name: String(record.filled_by_name ?? ''),
    filled_at: String(record.filled_at ?? ''),
  };
}

export function parseLocation(value: unknown): ServiceLocation | null {
  return value === 'taller' || value === 'domicilio' ? value : null;
}

/** Título del registro: recepción en el taller o estado del equipo en el domicilio. */
export function checkTitle(moment: PhysicalCheckRecord['moment']): string {
  return moment === 'recepcion' ? 'Comprobante de recepción' : 'Estado del equipo en el domicilio';
}

/** Título del PDF: el del checklist si existe; si no, según el lugar de atención. */
export function receiptTitle(location: ServiceLocation, record: PhysicalCheckRecord | null): string {
  if (record) return checkTitle(record.moment);
  return location === 'taller' ? 'Comprobante de recepción' : 'Orden de servicio a domicilio';
}

/** Texto del botón del PDF en la orden. */
export function receiptButtonLabel(location: ServiceLocation, record: PhysicalCheckRecord | null): string {
  if (record) return record.moment === 'recepcion' ? 'PDF de recepción' : 'PDF del estado del equipo';
  return location === 'taller' ? 'PDF de recepción' : 'PDF de la orden de servicio';
}
